import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { titleCase } from "../../utils/title-case";
import { emailDiscountCard } from "../../api/price-prescriptions";
import HeartIcon from "../../styles/default/assets/icons/heart.svg";
import EmptyHeartIcon from "../../styles/default/assets/icons/heart-empty.svg";
import PricePrescriptionDisclaimer from "./price-prescription-disclaimer";

const PricePrescriptionsDetails = ({
  drugPrice,
  discountCard,
  onBack,
  components,
  addToFavorite,
  favoriteList,
  profileId
}) => {
  const { Name, Price, PharmacyName, Street1, City, State, ZipCode } =
    drugPrice;
  const { memberId, group, pcn, bin } = discountCard;
  const { Title, Text, Icon, PhoneLink } = components;

  const detailsItems = [
    ["Member ID", memberId],
    ["Group", group],
    ["PCN", pcn],
    ["BIN", bin]
  ];
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isFavorite, setIsFavorite] = useState(
    (favoriteList || []).some(i => i.PharmacyId === drugPrice.PharmacyId)
  );

  const handleFavorite = useCallback(() => {
    const index = favoriteList.findIndex(
      item => item.PharmacyId === drugPrice.PharmacyId
    );
    if (index === -1) {
      addToFavorite({ favorite: true, meta: drugPrice });
      favoriteList.push(drugPrice);
      setIsFavorite(true);
    } else {
      addToFavorite({ favorite: false, meta: drugPrice });
      favoriteList.splice(index, 1);
      setIsFavorite(false);
    }
  }, [drugPrice]);

  const emailCard = () => {
    const payload = {
      ProfileId: profileId,
      PharmacyName,
      Address: Street1,
      City,
      State,
      Zip: ZipCode,
      DrugName: Name,
      MemberId: memberId,
      Pcn: pcn,
      Group: group,
      Bin: bin
    };
    emailDiscountCard(payload);
  };

  return (
    <>
      <button
        type='button'
        className='button-left-arrow-web-sdk'
        onClick={onBack}
      >
        Back
      </button>
      <Title title='Prescription details' />
      <div className='price-prescription-details-web-sdk'>
        {!showDisclaimer ? (
          <div className='price-prescription-details-svg' />
        ) : null}

        <div className='price-prescription-info-block-web-sdk'>
          {showDisclaimer ? (
            <PricePrescriptionDisclaimer
              onClose={() => setShowDisclaimer(false)}
            />
          ) : (
            <div className='websdk-flex-col websdk-min-w-full'>
              <div className='websdk-flex websdk-justify-between'>
                <div className='websdk-flex-col'>
                  <Text
                    params='price-prescription-name-text'
                    label={titleCase(Name)}
                  />
                  <Text
                    params='price-prescription-price-text'
                    label={`$${Price.toFixed(2)}`}
                  />
                </div>
                <div>
                  {!!(addToFavorite && favoriteList) && (
                    <Icon
                      showIcon
                      src={isFavorite ? HeartIcon : EmptyHeartIcon}
                      onClick={handleFavorite}
                    />
                  )}
                </div>
              </div>
              <Text
                params='websdk-text-xl websdk-mt-6 websdk-mb-2'
                label='Locations'
              />
              <Text
                params='price-prescription-pharmacy-text'
                label={titleCase(PharmacyName)}
              />
              <div className='websdk-flex websdk-justify-between websdk-mb-4'>
                <div className='websdk-flex websdk-flex-col websdk-justify-around'>
                  <Text
                    params='price-prescription-address-text'
                    label={`${titleCase(Street1)}`}
                  />
                  <Text
                    params='price-prescription-address-text'
                    label={`${titleCase(City)} ${State} ${ZipCode}`}
                  />
                </div>
              </div>
              <div className='websdk-flex websdk-flex-col websdk-mb-4'>
                <Text
                  params='price-prescription-phone-text'
                  label='Discount help line'
                />
                <PhoneLink
                  params='price-prescription-phone-number'
                  label='(855) 569-6337 Call'
                  link='(855) 569-6337'
                />
              </div>
              <Text
                params='websdk-text-xl websdk-mt-4 websdk-mb-0'
                label='Discount details'
              />
              <Text
                params='websdk-text-sm websdk-mt-0 websdk-mb-4 websdk-mr-2'
                label={`Show your pharmacist at ${titleCase(
                  PharmacyName
                )} to receive the best price on ${titleCase(Name)}`}
              />

              <div className='websdk-flex websdk-items-center websdk-justify-between websdk-h-12 websdk-mb-10'>
                {detailsItems.map(([title, text]) => (
                  <div className='websdk-flex websdk-flex-col' key={title}>
                    <Text
                      params='websdk-text-gray-500 websdk-mb-1'
                      label={title}
                    />
                    <Text params='websdk-mb-1' label={text} />
                  </div>
                ))}
                <div className='websdk-flex websdk-items-center'>
                  <Text
                    params='websdk-mb-0'
                    label='Email details'
                    onClick={emailCard}
                  />
                  <div className='price-prescription-details-send-email-svg' />
                </div>
              </div>
              <p className='websdk-mb-12'>
                Please call individual locations to confirm pricing available.
              </p>
              <Text
                label='Disclaimer'
                onClick={() => setShowDisclaimer(true)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PricePrescriptionsDetails;

PricePrescriptionsDetails.propTypes = {
  drugPrice: PropTypes.object,
  discountCard: PropTypes.object,
  onBack: PropTypes.func,
  components: PropTypes.object,
  addToFavorite: PropTypes.func,
  favoriteList: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string
};
